import { z } from 'zod'
import {
  padSchema,
  partialPadSchema,
} from '@customTypes/cloudmix/sessions/pads'
import { componentSchema } from '@customTypes/cloudmix/sessions/components'
import type { Macro } from '@customTypes/cloudmix/sessions/macros'

/*
  COMMANDS
 */
export const SESSION_COMMANDS = {
  EnableRecording: 'enable_recording',
  DisableRecording: 'disable_recording',
  UpdateComponent: 'update_component',
  RemoveComponent: 'remove_component',
  RemoveLink: 'remove_link',
  AddLink: 'add_link',
  AddComponent: 'add_component',
  RemovePad: 'remove_pad',
  UpdatePad: 'update_pad',
  UpdatePads: 'update_pads',
  AddMacro: 'add_macro',
  UpdateMacro: 'update_macro',
  RemoveMacro: 'remove_macro',
  ExecuteMacroById: 'execute_macro_by_id',
  ExecuteMacro: 'execute_macro',
  StopMacro: 'stop_macro',
  ResetComponent: 'reset_component',
}
export const MIXER_COMMANDS = {
  AddInput: 'add_input',
  RemoveInput: 'remove_input',
  UpdateInput: 'update_input',
  UpdateInputs: 'update_inputs',
  ApplySceneById: 'apply_scene_by_id',
  ApplyScene: 'apply_scene',
} as const

export const PLAYER_SOURCE_COMMANDS = {
  play: 'play',
  pause: 'pause',
  stop: 'stop_player',
  setPosition: 'set_position',
} as const

export const LIVE_OUTPUT_DESTINATION_COMMANDS = {
  SendScteOut: 'send_scte_out',
  SendScteIn: 'send_scte_in',
  SendScteCancel: 'send_scte_cancel',
} as const

export const COMMANDS = {
  ...SESSION_COMMANDS,
  ...MIXER_COMMANDS,
  ...PLAYER_SOURCE_COMMANDS,
  ...LIVE_OUTPUT_DESTINATION_COMMANDS,
}

export const commandSchema = z.union([
  z.literal(COMMANDS.AddInput),
  z.literal(COMMANDS.RemoveInput),
  z.literal(COMMANDS.UpdateInput),
  z.literal(COMMANDS.UpdateComponent),
  z.literal(COMMANDS.RemoveComponent),
  z.literal(COMMANDS.RemoveLink),
  z.literal(COMMANDS.AddLink),
  z.literal(COMMANDS.AddComponent),
  z.literal(COMMANDS.RemovePad),
  z.literal(COMMANDS.UpdatePad),
  z.literal(COMMANDS.UpdatePads),
  z.literal(COMMANDS.AddMacro),
  z.literal(COMMANDS.UpdateMacro),
  z.literal(COMMANDS.RemoveMacro),
  z.literal(COMMANDS.ExecuteMacroById),
  z.literal(COMMANDS.ExecuteMacro),
  z.literal(COMMANDS.StopMacro),
  z.literal(COMMANDS.play),
  z.literal(COMMANDS.pause),
  z.literal(COMMANDS.stop),
  z.literal(COMMANDS.setPosition),
  z.literal(COMMANDS.ApplySceneById),
  z.literal(COMMANDS.ApplyScene),
  z.literal(COMMANDS.SendScteIn),
  z.literal(COMMANDS.SendScteOut),
  z.literal(COMMANDS.SendScteCancel),
  z.literal(COMMANDS.ResetComponent),
])

export type Command = z.infer<typeof commandSchema>

/*
  EVENTS
 */

export const EVENTS = {
  MixerUpdateInput: 'component.mixer.updateInput',
  RouterUpdateInput: 'component.router.updateInput', // This doesn't exist yet
}

export const eventSchema = z.union([
  z.literal(EVENTS.MixerUpdateInput),
  z.literal(EVENTS.RouterUpdateInput),
])

/*
  ACTIONS
 */

export const ACTIONS = {
  ProjectSessionCommand: 'command',
} as const

const baseActionCommandSchema = z.object({
  command: z.object({
    name: commandSchema,
  }),
  componentId: z.string().nullable(),
})

export const addMixerInputActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.AddInput),
    args: z.object({
      pad: padSchema,
    }),
  }),
})

export const removeMixerInputActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.RemoveInput),
    args: z.object({
      pad_name: z.string(),
    }),
  }),
})

export const updateComponentActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.UpdateComponent),
    args: z.object({
      values: z.record(z.string(), z.any()),
      component_id: z.string().optional(),
    }),
  }),
})

export const removeComponentActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.RemoveComponent),
    args: z.object({
      component_id: z.string(),
    }),
  }),
})

export const removeLinkActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.RemoveLink),
    args: z.object({
      link_id: z.string(),
    }),
  }),
})

export const addLinkActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.AddLink),
    args: z.object({
      link: z.object({
        source: z.object({
          componentId: z.string(),
          padName: z.string(),
        }),
        target: z.object({
          componentId: z.string(),
          padName: z.string(),
        }),
      }),
    }),
  }),
})

export const addComponentActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.AddComponent),
    args: z.object({
      component: componentSchema,
    }),
  }),
})

export const removePadActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.RemovePad),
    args: z.object({
      pad_name: z.string(),
    }),
  }),
})

export const updatePadActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.UpdatePad),
    args: z.object({
      pad_name: z.string(),
      values: partialPadSchema,
    }),
  }),
})

export const updatePadsActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.UpdatePads),
    args: z.object({
      values: z.record(z.string(), partialPadSchema),
    }),
  }),
})

export const addMacroActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.AddMacro),
    args: z.object({
      macro: z.custom<Macro>(),
    }),
  }),
})

export const removeMacroActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.RemoveMacro),
    args: z.object({
      macro_id: z.string(),
    }),
  }),
})

export const updateMacroActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.UpdateMacro),
    args: z.object({
      values: z.record(z.string(), z.any()),
      macro_id: z.string(),
    }),
  }),
})

export const executeMacroByIdActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.ExecuteMacroById),
    args: z.object({
      macro_id: z.string(),
    }),
  }),
})

export const executeMacroActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.ExecuteMacro),
    args: z.object({
      macro: z.custom<Macro>(),
    }),
  }),
})

export const stopMacroActionSchema = baseActionCommandSchema.extend({
  componentId: z.null(),
  command: z.object({
    name: z.literal(COMMANDS.StopMacro),
    args: z.object({
      macro_id: z.string(),
    }),
  }),
})

export const actionCommandsSchema = z.union([
  addMixerInputActionSchema,
  removeMixerInputActionSchema,
  updateComponentActionSchema,
  removeComponentActionSchema,
  removeLinkActionSchema,
  addLinkActionSchema,
  addComponentActionSchema,
  removePadActionSchema,
  updatePadActionSchema,
  updatePadsActionSchema,
  addMacroActionSchema,
  updateMacroActionSchema,
  removeMacroActionSchema,
  executeMacroByIdActionSchema,
  executeMacroActionSchema,
  stopMacroActionSchema,
])

export type ActionCommands = z.infer<typeof actionCommandsSchema>

export const applySceneByIdActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.ApplySceneById),
    args: z.object({
      scene_id: z.string(),
      transition_duration_ms: z.number().nullish(),
    }),
  }),
})
export const sendScteOutSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.SendScteOut),
    args: z.object({
      splice_time_ms: z.number().optional(),
      duration_ms: z.number(),
    }),
  }),
})

export const sendScteInSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.SendScteIn),
    args: z.object({
      event_id: z.number(),
      splice_time_ms: z.number().optional(),
    }),
  }),
})

export const sendScteCancelSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.SendScteCancel),
    args: z.object({
      event_id: z.number(),
    }),
  }),
})

export const resetComponentActionSchema = baseActionCommandSchema.extend({
  componentId: z.string(),
  command: z.object({
    name: z.literal(COMMANDS.ResetComponent),
    args: z.object({}),
  }),
})
